/* eslint-disable react-refresh/only-export-components -- intentional: come ActiveMatchContext, il
   provider e il suo hook (`useSfideBarra`) vivono nello stesso file per costruzione — separarli
   servirebbe solo a spegnere questo avviso, non a chiarire niente. */
import { createContext, lazy, Suspense, useCallback, useContext, useMemo, useState, type ComponentType, type ReactNode } from 'react';

import { SorgenteSfidePiattaforma } from '@/contexts/SorgenteSfidePiattaforma';
import { env } from '@/env';
import { useSfidaDiPiattaforma } from '@/hooks/useSfidaDiPiattaforma';
import type { LimuloAzionePendente } from '@/lib/limulo/azionePersistente';

/**
 * **La fonte delle sfide per la barra, FUORI da `AzionePersistente`** (I-252/I-254, T2-5 —
 * disegno (b), scelto da `tma-orcr` il 28/9 18:07Z contro (a) che la teneva dentro).
 *
 * ⛔ **Il motivo del file a se'**: `AzionePersistente.tsx` e i suoi banchi di test (di proprieta' di
 * `tma-orcr`) non devono importare la lettura delle sfide (`limuloApi`; `dmApi`/`socialApi` non li
 * importa piu' nemmeno il provider) — provato montando `<AzionePersistente>` da solo, senza questo
 * provider: `useContext` con un default statico non tocca ne' Redux ne' RTK
 * Query, quindi i banchi restano IDENTICI e verdi senza saperlo. Chi vuole la sfida nella barra
 * avvolge con `<SfideBarraProvider>` (fatto in `AppShell.tsx`, attorno alla sola riga che monta
 * `AzionePersistente`): tutto il resto dell'app non vede questo contesto e non ne paga il costo.
 */
const SfideBarraContext = createContext<readonly LimuloAzionePendente[]>([]);

/** Cio' che `AzionePersistente` legge: `[]` se nessun provider e' montato (i banchi di tma-orcr). */
export function useSfideBarra(): readonly LimuloAzionePendente[] {
  return useContext(SfideBarraContext);
}

/**
 * **UNA fonte** (lotto C2): la sfida di PIATTAFORMA (`GET /v1/challenges`, `SorgenteSfidePiattaforma`).
 * Dietro la sua serratura, `useSfidaDiPiattaforma()` — Limulo + ruolo beta, env/ruolo e MAI un hook RTK:
 * fuori serratura `SorgenteSfidePiattaforma` non si monta e non parte nessuna `GET /v1/challenges`
 * [condizione 2 di tma-orcr su B1], e il provider rende SOLO `children`.
 * La lista dei DM non e' piu' una fonte della barra: la barra non la interroga, su nessuna schermata
 * [misurato in `tests/sfide-barra-piattaforma-costo.test.tsx`, col controllo positivo].
 */
export function SfideBarraProvider({ children }: { children: ReactNode }) {
  const piattaforma = useSfidaDiPiattaforma();
  return piattaforma ? (
    <SfideBarraProviderAttivo>{children}</SfideBarraProviderAttivo>
  ) : (
    <>{children}</>
  );
}

// PONTE PROVVISORIO I-1317 (A4: il modulo lazy della fonte, SOLO nel letterale) — si toglie quando: P-a (qTournaments#225 e qlash-limulo#287 MERGED, `GET /v1/challenges/<id>` su staging porta `allenamento`, `src/lib/limulo/challenge.ts` lo manda).
const FontePonteI1317BarraLazy =
  import.meta.env.VITE_FEATURE_BACKGAMMON_COACH_ENABLED === 'true'
    ? lazy(() =>
        import('@/components/backgammon/allenamento/PonteI1317')
          .then((m): { default: ComponentType<{ riporta: (chiave: string, riga: LimuloAzionePendente | null) => void }> } => ({
            default: m.FontePonteI1317Barra,
          }))
          .catch(() => ({ default: () => null })),
      )
    : null;

function SfideBarraProviderAttivo({ children }: { children: ReactNode }) {
  const [righe, setRighe] = useState<Record<string, LimuloAzionePendente>>({});
  const riporta = useCallback((chiave: string, riga: LimuloAzionePendente | null) => {
    setRighe((prev) => {
      if (riga === null) {
        if (!(chiave in prev)) return prev;
        const resto = { ...prev };
        delete resto[chiave];
        return resto;
      }
      return prev[chiave] === riga ? prev : { ...prev, [chiave]: riga };
    });
  }, []);
  const sfide = useMemo(() => Object.values(righe), [righe]);

  return (
    <SfideBarraContext.Provider value={sfide}>
      {/* PONTE PROVVISORIO I-1317 (A4: la fonte della barra, B1, forma (ii)) — si toglie quando: P-a (qTournaments#225 e qlash-limulo#287 MERGED, `GET /v1/challenges/<id>` su staging porta `allenamento`, `src/lib/limulo/challenge.ts` lo manda). A letterale spento il ramo si piega nella sola sorgente di sempre, nella stessa posizione (nessun figlio in piu'). */}
      {import.meta.env.VITE_FEATURE_BACKGAMMON_COACH_ENABLED === 'true' && FontePonteI1317BarraLazy && env.featureDmEnabled && env.featureBackgammonEnabled ? (
        <>
          <SorgenteSfidePiattaforma riporta={riporta} />
          <Suspense fallback={null}>
            <FontePonteI1317BarraLazy riporta={riporta} />
          </Suspense>
        </>
      ) : (
        <SorgenteSfidePiattaforma riporta={riporta} />
      )}
      {children}
    </SfideBarraContext.Provider>
  );
}
